import type { InvestigationBillLookupOption, PatientFormFields } from './types';
import { doctorOptionLabel } from '../../lib/doctor-name';
import { normalizePhoneInput } from '../../lib/phone-input';

export const formatInvestigationDateTime = (value: string) => new Intl.DateTimeFormat('en-IN', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
}).format(new Date(value));

export const currency = (value: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);

const amountWordsOnes = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const amountWordsTens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const amountWordsTwoDigits = (value: number): string => {
  if (value < 20) return amountWordsOnes[value] ?? '';
  const tens = amountWordsTens[Math.floor(value / 10)] ?? '';
  const ones = value % 10 ? ` ${amountWordsOnes[value % 10]}` : '';
  return `${tens}${ones}`;
};

const amountWordsThreeDigits = (value: number): string => {
  const hundreds = Math.floor(value / 100);
  const rest = value % 100;
  return [hundreds ? `${amountWordsOnes[hundreds]} Hundred` : '', rest ? amountWordsTwoDigits(rest) : ''].filter(Boolean).join(' ');
};

export const amountInWords = (amount: number): string => {
  const value = Math.floor(Math.abs(amount));
  if (value === 0) return 'Zero Rupees Only';
  const crore = Math.floor(value / 1_00_00_000);
  const lakh = Math.floor((value % 1_00_00_000) / 1_00_000);
  const thousand = Math.floor((value % 1_00_000) / 1000);
  const hundred = value % 1000;
  const parts = [
    crore ? `${amountWordsThreeDigits(crore)} Crore` : '',
    lakh ? `${amountWordsTwoDigits(lakh)} Lakh` : '',
    thousand ? `${amountWordsTwoDigits(thousand)} Thousand` : '',
    hundred ? amountWordsThreeDigits(hundred) : '',
  ].filter(Boolean);
  return `${parts.join(' ')} Rupees Only`;
};

export const todayInput = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10);
};

export const dateTimeInput = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

export const optionList = (items: InvestigationBillLookupOption[] | undefined) => (items ?? []).map((item) => ({ value: item.id, label: doctorOptionLabel(item.label, item.code) }));

export const blankPatientFields = (): PatientFormFields => ({
  patientId: null, uhid: null, name: '', phone: null, gender: 'Male', ageYears: 0, ageMonths: 0, ageDays: 0,
  address: null, state: 'West Bengal (WB)', district: null, pinCode: null,
});

export const patientFieldsFromOption = (option: InvestigationBillLookupOption): PatientFormFields => {
  let ageYears = 0;
  if (option.dateOfBirth) {
    const birth = new Date(option.dateOfBirth);
    const now = new Date();
    ageYears = Math.max(0, now.getFullYear() - birth.getFullYear() - (now < new Date(now.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0));
  }
  return {
    patientId: option.id,
    uhid: option.code ?? null,
    name: option.label,
    phone: option.phone ? normalizePhoneInput(option.phone) : null,
    gender: option.gender === 'female' ? 'Female' : option.gender === 'male' ? 'Male' : 'Others',
    ageYears,
    ageMonths: 0,
    ageDays: 0,
    address: option.address ?? null,
    state: option.state ?? 'West Bengal (WB)',
    district: option.district ?? null,
    pinCode: option.pinCode ?? null,
  };
};
